import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import 'react-phone-number-input/style.css'
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber, PhoneInputProps } from 'react-phone-number-input'
import { useDispatch, useSelector } from 'react-redux';
import { requestVerifyCode } from 'modules/actions/guestUser';
import { getGuestUserState } from 'modules/selectors/guestUser';
import loader from 'assets/img/landing/loader.svg';
import { rotation } from 'components/keyFrames';
import { CheckboxStyled } from 'components/CheckboxStyled';
import { partial } from 'lodash';
import { NavLink } from 'react-router-dom';

const Wrapper = styled.div`
	padding: 30px 120px 60px;
	@media(max-width: 1000px) {
	    padding: 30px 10px;
	    width: 100%;
	}
`;

const Title = styled.div`
	font-size: 32px;
	color: #FEC600;
	line-height: 42px;
	font-family: 'ImpactBold', sans-serif;
	text-transform: uppercase;
	text-align: center;
	margin-bottom: 10px;
`;
const SubTitle = styled.div`
	font-size: 12px;
	color: #fff;
	line-height: 20px;
	font-family: 'EundBold', sans-serif;
	text-align: center;
	margin-bottom: 40px;
`;

const PhoneInputWrapper = styled.form`
	display: flex;
	align-items: center;
	justify-content: center;
	@media(max-width: 1000px) {
	    flex-direction: column;
	    justify-content: center;
	    align-items: center;
	}
`;
const ErrorStyles = css`
	.PhoneInputCountry {
		background: #C3470E;
		color: #ffffff;
	}
	.PhoneInputInput {
		border-color: #C3470E;
		color: #C3470E;
	}
`;

interface IPhoneInputStyled extends PhoneInputProps {
	iserror: string;
}

const PhoneInputStyled = styled( PhoneInput )<IPhoneInputStyled>`
	display: flex;
	width: 100%;
	.PhoneInputCountry {
		width: 89px;
		height: 50px;
		background: #EDECE8;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0;
	}
	.PhoneInputInput {
		height: 48px;
		border: 1px solid #EDECE8;
		padding: 0 13px;
		font-size: 16px;
		font-family: 'EundBold', sans-serif;
		min-width: 300px;
		@media(max-width: 1000px) {
			min-width: auto;
			margin-bottom: 20px;
			flex: 1;
		}
	}
	${ props => !!props.iserror && ErrorStyles };
`;
const ButtonSection = styled.div`
	margin-left: 20px;
	@media(max-width: 1000px) {
		width: 100%;
		margin: 0;
	}
`;
const Button = styled.button`
	width: 140px;
	height: 50px;
	background: #FEC600;
	border-radius: 2px;
	font-family: 'ImpactBold', sans-serif;
	border: none;
	font-size: 30px;
	color: #362E2C;
	&:disabled {
		background: #E3E1DB;
		opacity: .2;
	}
	@media(max-width: 1000px) {
		width: 100%;
	}
`;

const Loader = styled.img`
    width: 100px;
    margin: 0 auto;
    display: block;
	animation: ${ rotation } 2s linear infinite;
`;

const OptInText = styled.div`
	color: #fff;
	font-size: 12px;
	margin-bottom: 14px;
`;

const SubText = styled.div`
	size: 9px;
	font-family: 'Eund', sans-serif;
	font-style: italic;
	a {
		color: #FEC600;
	}
`;

export const PhoneEnterSection = () => {
	const dispatch = useDispatch();
	const { showLoader, error: storeError } = useSelector( getGuestUserState );
	const [value, setValue] = useState( '' );
	const [error, setError] = useState( '' );
	const [optIn, changeOptIn] = useState( false );

	useEffect( () => {
		if ( storeError !== error ) {
			setError( error );
		}
	}, [storeError, error] )

	const submitPhone = ( e: React.FormEvent ) => {
		e.preventDefault();
		const isValid = isValidPhoneNumber( value );
		const phoneNumber = parsePhoneNumber( value );
		if ( !isValid || !phoneNumber ) {
			setError( 'Phone Invalid' )
			return;
		}

		dispatch( requestVerifyCode( {
			mobile: phoneNumber.nationalNumber,
			country_code: phoneNumber.countryCallingCode,
			marketing: Number( optIn ),
		} ) );
	}

	const changePhoneHandler = ( phoneNum: string ) => {
		setError( '' );
		setValue( phoneNum );
	}
	const isButtonsDisabled = !!error || !value;
	return (
		<Wrapper>
			<Title>To play as a Guest</Title>
			<SubTitle>Simply, enter your phone number and we'll send you a code so we can start to identify you on
				leaderboards.</SubTitle>
			{ !showLoader ? <PhoneInputWrapper onSubmit={ submitPhone }>
				<PhoneInputStyled
					international={ true }
					addInternationalOption={ false }
					defaultCountry={ 'US' }
					placeholder="Enter phone number"
					value={ value }
					onChange={ changePhoneHandler }
					iserror={ error }
				/>
				<ButtonSection>
					<Button disabled={ isButtonsDisabled }>SUBMIT</Button>
				</ButtonSection>
				<CheckboxStyled onChange={ partial( changeOptIn, !optIn ) } name={ 'opt_in' }>
					<OptInText>
						By ticking this box I opt-in to receive marketing related text messages from Buffalo Wild Wings
						on upcoming offers and promotions.
					</OptInText>
					<SubText>
						I agree to receive autodialed texts about existing offers and other information from Buffalo
						Wild Wings. Consent not required/condition for play. Not all carriers covered. Msg. and data
						rates apply. Text STOP to stop (and text will be sent or email support@blazinrewards.com) and
						HELP for help.<br/><br/>
						I understand my information will be used as described in the
						<a href="https://www.buffalowildwings.com/en/privacy-policy-terms/"> Privacy Policy </a>and that
						the
						<NavLink to={ '/rules' }> game rules </NavLink> apply
					</SubText>
				</CheckboxStyled>
			</PhoneInputWrapper> : <Loader src={ loader } role={ 'presentation' }/> }
		</Wrapper>
	);
}
